/**
 * Session Token Authentication Utility
 *
 * Manages session tokens for pre-registration authentication flow.
 * Session tokens are obtained after OTP verification and used until full registration.
 * Tokens are stored in cookies (Secure; SameSite=Strict) instead of localStorage.
 */

import { getCookie, setCookie, removeCookie } from './cookie-storage';

const SESSION_TOKEN_KEY = 'session_token';
const SESSION_TOKEN_MAX_AGE = 24 * 60 * 60; // 24 hours in seconds

export interface SessionTokenData {
  token: string;
  expiresAt: string; // ISO date string
  phoneNumber?: string;
  email?: string;
}

/**
 * Store session token in a Secure; SameSite=Strict cookie
 */
export const storeSessionToken = (
  token: string,
  expiresAt?: string,
  phoneNumber?: string,
  email?: string
): void => {
  try {
    const expiry = expiresAt || new Date(Date.now() + SESSION_TOKEN_MAX_AGE * 1000).toISOString();

    const sessionData: SessionTokenData = {
      token,
      expiresAt: expiry,
      phoneNumber,
      email,
    };

    setCookie(SESSION_TOKEN_KEY, JSON.stringify(sessionData), SESSION_TOKEN_MAX_AGE);
  } catch (error) {
    console.error('[SessionAuth] Failed to store session token:', error);
  }
};

/**
 * Get session token from cookie
 */
export const getSessionToken = (): string | null => {
  try {
    const sessionDataStr = getCookie(SESSION_TOKEN_KEY);
    if (!sessionDataStr) return null;

    let sessionData: SessionTokenData;
    try {
      sessionData = JSON.parse(sessionDataStr);
    } catch {
      clearSessionToken();
      return null;
    }

    if (new Date(sessionData.expiresAt) < new Date()) {
      clearSessionToken();
      return null;
    }

    return sessionData.token;
  } catch (error) {
    console.error('[SessionAuth] Failed to get session token:', error);
    clearSessionToken();
    return null;
  }
};

/**
 * Get full session data from cookie
 */
export const getSessionData = (): SessionTokenData | null => {
  try {
    const sessionDataStr = getCookie(SESSION_TOKEN_KEY);
    if (!sessionDataStr) return null;

    let sessionData: SessionTokenData;
    try {
      sessionData = JSON.parse(sessionDataStr);
    } catch {
      clearSessionToken();
      return null;
    }

    if (new Date(sessionData.expiresAt) < new Date()) {
      clearSessionToken();
      return null;
    }

    return sessionData;
  } catch (error) {
    console.error('[SessionAuth] Failed to get session data:', error);
    clearSessionToken();
    return null;
  }
};

/**
 * Clear session token cookie
 */
export const clearSessionToken = (): void => {
  try {
    removeCookie(SESSION_TOKEN_KEY);
  } catch (error) {
    console.error('[SessionAuth] Failed to clear session token:', error);
  }
};

/**
 * Check if user has a valid session token
 */
export const hasValidSessionToken = (): boolean => {
  return getSessionToken() !== null;
};

/**
 * Get time until session token expires (in milliseconds)
 */
export const getSessionTokenTimeRemaining = (): number => {
  try {
    const sessionData = getSessionData();
    if (!sessionData) return 0;
    return new Date(sessionData.expiresAt).getTime() - Date.now();
  } catch (error) {
    console.error('[SessionAuth] Failed to get session token time remaining:', error);
    return 0;
  }
};

/**
 * Check if user is in pre-registration phase (has session token but not JWT).
 * Detects Amplify JWT presence by scanning cookies for Cognito access token keys.
 */
export const isPreRegistrationPhase = (): boolean => {
  const hasSessionToken = hasValidSessionToken();
  const hasJWT = (() => {
    try {
      if (typeof document === 'undefined') return false;
      return document.cookie.split(';').some((part) => {
        const key = decodeURIComponent(part.trim().split('=')[0]);
        return key.includes('accessToken') || key.includes('idToken');
      });
    } catch {
      return false;
    }
  })();

  return hasSessionToken && !hasJWT;
};

/**
 * Format session token for Authorization header
 */
export const formatSessionTokenHeader = (token: string): string => {
  return `Session ${token}`;
};

/**
 * Get authentication headers for API calls.
 * Uses session token for pre-registration flow; post-registration JWT is
 * handled by the axios request interceptor in index.tsx.
 */
export const getAuthHeaders = (): Record<string, string> => {
  const sessionToken = getSessionToken();
  if (sessionToken) {
    return {
      'X-Session-Token': sessionToken,
      Authorization: formatSessionTokenHeader(sessionToken),
    };
  }
  return {};
};
